import { Edit, Eye, GitBranch, LoaderCircle, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { PreviewPromptForm } from './PrompPreview/PromptPreview';
import { RemovePromptDialog } from './RemovePrompt';
import { EditPromptForm } from './UpdatePrompt';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TableCellWithTooltip,
} from '@/components/ui/Table';
import { useGetPrompts } from '@/hooks/useGetPrompts';
import { useSetPromptAsMain } from '@/hooks/useSetPromptAsMain';
import { FilterPromptsDto, Prompt } from '@/services/PromptsService';

interface PromptTableProps {
  filter: FilterPromptsDto;
}

export function PromptsTable({
  filter,
}: {
  filter: PromptTableProps['filter'];
}) {
  const endPromptsRef = useRef<HTMLDivElement>(null);

  const [showEditPrompt, setShowEditPrompt] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showPreviewPrompt, setShowPreviewPrompt] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);

  const {
    prompts,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetPrompts({
    serviceName: filter.serviceName,
    promptName: filter.promptName,
    customerId: filter.customerId,
  });

  const { setPromptAsMain, isSettingAsMain } = useSetPromptAsMain(filter);

  useEffect(() => {
    if (!endPromptsRef.current) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        const { isIntersecting } = entries[0];

        if (!hasNextPage) {
          obs.disconnect();
          return;
        }

        if (isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: '30px',
      },
    );

    observer.observe(endPromptsRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  function handleSuccess() {
    setShowEditPrompt(false);
    setSelectedPrompt(null);
    toast.success('Prompt updated successfully!');
  }

  function handleEditPrompt(prompt: Prompt) {
    setSelectedPrompt(prompt);
    setShowEditPrompt(true);
  }

  function handleRemovePrompt(prompt: Prompt) {
    setSelectedPrompt(prompt);
    setShowRemoveDialog(true);
  }

  function handleCloseEdit() {
    setShowEditPrompt(false);
    setSelectedPrompt(null);
  }

  function handleCloseRemove() {
    setShowRemoveDialog(false);
    setSelectedPrompt(null);
  }

  function handlePreviewPrompt(prompt: Prompt) {
    setSelectedPrompt(prompt);
    setShowPreviewPrompt(true);
  }

  function handleClosePreview() {
    setShowPreviewPrompt(false);
    setSelectedPrompt(null);
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Service Name</TableHead>
            <TableHead>Prompt Name</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Template</TableHead>
            <TableHead>Response Schema </TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Main</TableHead>
            <TableHead>Variables</TableHead>
            <TableHead>Updated At</TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prompts.length === 0 && !isFetching && (
            <TableRow>
              <TableCell
                colSpan={11}
                className="py-10 text-center text-gray-500"
              >
                No prompts found.
              </TableCell>
            </TableRow>
          )}
          {prompts.map((prompt) => (
            <TableRow key={prompt.id} className="hover:bg-gray-100">
              <TableCellWithTooltip className="font-medium">
                {prompt.id}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="font-medium">
                {prompt.serviceName}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="font-medium">
                {prompt.promptName}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="font-medium">
                {prompt.customerId ?? 'global'}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="max-w-lg truncate">
                <div className="text-md">{prompt.template.slice(0, 40)}</div>
              </TableCellWithTooltip>
              <TableCellWithTooltip className="max-w-lg truncate">
                <div className="text-sm">
                  {prompt.responseSchema
                    ? JSON.stringify(prompt.responseSchema).slice(0, 20) + '...'
                    : '-'}
                </div>
              </TableCellWithTooltip>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="gap-1">
                    <GitBranch size={16} />v{prompt.version}
                  </Badge>
                </div>
              </TableCell>

              <TableCell>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={prompt.main}
                    onCheckedChange={() => setPromptAsMain(prompt.id as string)}
                    disabled={isSettingAsMain}
                    className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-400"
                  />
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1.5">
                  {prompt.variables?.slice(0, 2).map((variable) => (
                    <Badge
                      key={variable}
                      variant="outline"
                      className="border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 text-xs text-blue-800 transition-all duration-200 hover:from-blue-100 hover:to-indigo-100"
                    >
                      {variable}
                    </Badge>
                  ))}
                  {prompt.variables && prompt.variables?.length > 2 && (
                    <Badge
                      variant="outline"
                      className="border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 text-xs text-blue-800 transition-all duration-200 hover:from-blue-100 hover:to-indigo-100"
                    >
                      <span className="flex items-center gap-1">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        +{prompt.variables.length - 3}
                      </span>
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCellWithTooltip className="text-sm text-gray-600">
                {new Date(prompt.updatedAt || '').toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </TableCellWithTooltip>
              <TableCell className="text-center">
                <div className="flex items-center justify-start gap-2">
                  <Button
                    onClick={() => handlePreviewPrompt(prompt)}
                    variant="ghost"
                    size="sm"
                    className="hover:bg-white"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => handleEditPrompt(prompt)}
                    variant="ghost"
                    size="sm"
                    className="hover:bg-white"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => handleRemovePrompt(prompt)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-white hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div ref={endPromptsRef} />

      {(isFetching || isFetchingNextPage) && (
        <div className="flex justify-center py-4">
          <LoaderCircle className="h-6 w-6 animate-spin" />
        </div>
      )}

      {selectedPrompt && (
        <>
          <EditPromptForm
            open={showEditPrompt}
            onClose={handleCloseEdit}
            prompt={selectedPrompt}
            onSuccess={handleSuccess}
            filter={filter}
          />
          <RemovePromptDialog
            open={showRemoveDialog}
            onClose={handleCloseRemove}
            prompt={selectedPrompt}
            filter={filter}
          />
          <PreviewPromptForm
            open={showPreviewPrompt}
            onClose={handleClosePreview}
            prompt={selectedPrompt}
          />
        </>
      )}
    </div>
  );
}
